/*
 * @Author: changdou.fe@bytedance.com
 * @Date: 2025-10-02 21:00:00
 * @LastEditors: changdou.fe@bytedance.com
 * @LastEditTime: 2026-04-11 11:47:58
 * @Description: 一期仅支持合约充值和提现，隐藏其他逻辑
 */
import BN from 'bignumber.js';
import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsProps, Modal, Tooltip, Select, QRCode, Collapse, Steps, Input, message, Button } from 'antd';
import { Info, HelpCircle, Clock, CircleDollarSign, Wallet } from 'lucide-react';
import { SwitchIcon } from '@/app/const/svg';
import { useTranslation } from 'react-i18next';
import { useSignTypedData } from '@privy-io/react-auth';
import { useSessionSignerAuth } from '@/hooks/wallet/use-session-signer-auth';
import { useSetReferrer } from '@/hooks/wallet/use-set-referrer';
import { usePerpsWallet } from '@/hooks/wallet/use-perps-wallet';
import { useSafeAddress, usePredictDeposit, usePortfolio } from '@/app/predict/hooks';
import { chainList, defaultChain, arbitrumUSDC, convertChains, perpsUSDC } from '@/app/const/chain';
import { useUserInfo } from '@/app/stores/useUserStore';
import { ChainInfo, TokenInfo, convertExecute, getConvertQuote, getAllTokens, spotExchange } from '@/lib/dex-api';
import { getClearinghouseState } from '@/lib/hl-api';
import { getSupportedAssets, postBridgeDeposit } from '@/lib/predict-api';
import { divide, validAmount } from '@/lib/utils';
import { formatPriceWithoutComma, submitPerpsWithdraw3 } from '@/app/perps/util';
import { Skeleton } from '@/components/ui/skeleton';
import CopyText from '@/components/copy';
import RiskModal from '@/components/risk-modal';
import Chain from '../chain';
import Token from '../token';
import { BITCOIN_CHAIN_ID, convertPdAssets, pdTokenLogos, PredictBridgeChainItem, SOLANA_CHAIN_ID, TRON_CHAIN_ID } from './utils';
import { captureDepositClick, capturePaymentResult } from '@/lib/analytics/posthog-events';
import './index.scss';

const COUNTDOWN_TIME = 60;
// 动态加载 SetupGuideModal，避免将 @pd/clob-client 等重依赖拉入划转弹窗 bundle
const SetupGuideModal = lazy(() => import('@/app/predict/components/setup-guide').then(m => ({ default: m.SetupGuideModal })));

export type DepositType = 'spot' | 'perps' | 'predict';

interface DepositV2Props {
  defaultChainId?: string;
  defaultTokenAddress?: string;
  depositTab?: DepositType;
  /** 埋点：充值入口来源，由调用方传入 */
  source?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // 划转成功回调，用于通知父组件刷新数据
}

const DepositV2: React.FC<DepositV2Props> = ({ defaultChainId, defaultTokenAddress, depositTab = 'spot', source, open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prevDepositOpen = useRef(false);
  const userInfo = useUserInfo();
  const evmAddress = userInfo?.evmWalletAddress;

  /* -------------------------------- 充值相关 start -------------------------------- */
  // 充值的类型
  const [type, setType] = useState<DepositType | ''>('');
  // 现货充值：选中的网络与币种（deposit-token-list-wrap）
  const [depositSelectedChain, setDepositSelectedChain] = useState<ChainInfo>(chainList[0]);
  const [depositSelectedToken, setDepositSelectedToken] = useState<TokenInfo | null>(chainList[0].tokenList[0]);
  /* -------------------------------- 充值相关 end -------------------------------- */

  /* -------------------------------- 划转相关 start -------------------------------- */
  // ✅ 使用 Privy 的合约钱包 Hook
  const { checkAndAuthorize } = useSessionSignerAuth();
  const { checkAndSetReferrer } = useSetReferrer();
  const { switchChain, isReady } = usePerpsWallet();
  const { signTypedData } = useSignTypedData();
  const isLogin = Boolean(userInfo?.evmWalletAddress || userInfo?.solWalletAddress);
  // perps 两步：0=充值地址，1=现货划转
  const [perpsStep, setPerpsStep] = useState(0);
  // 兑换（perps 第二步）相关状态
  const [initLoading, setInitLoading] = useState(true);
  const [chainFrom, setChainFrom] = useState<ChainInfo>(defaultChain);
  const [chainTo, setChainTo] = useState<ChainInfo>(convertChains[convertChains.length - 1]);
  const [tokenFrom, setTokenFrom] = useState<TokenInfo>({} as TokenInfo);
  const [tokenTo, setTokenTo] = useState<TokenInfo>(perpsUSDC);
  const [amountFrom, setAmountFrom] = useState('');
  const [amountTo, setAmountTo] = useState('');
  const [priceData, setPriceData] = useState<any>({});
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [showSwapDetail, setShowSwapDetail] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownAmountRef = useRef<string>('');
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [buttonText, setButtonText] = useState(() => t('exchange.convert'));
  const [errMsg, setErrMsg] = useState('');
  const [estimateGasFee, setEstimateGasFee] = useState(0);
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const debounceFetchRef = useRef<NodeJS.Timeout | null>(null);
  const isSame = useMemo(
    () => tokenFrom?.chainId === tokenTo?.chainId && tokenFrom?.tokenContractAddress === tokenTo?.tokenContractAddress,
    [tokenFrom, tokenTo]
  );
  /* -------------------------------- 划转相关 end -------------------------------- */

  /* -------------------------------- Predict 充值相关 start -------------------------------- */
  // Safe 地址
  const { safeAddress, recheckDeployment } = useSafeAddress();
  // Predict 充值 Hook
  const { refreshBalance: refreshPredictBalance } = usePredictDeposit(evmAddress, safeAddress);
  // Safe 余额（用于显示目标余额）
  const { refresh: refreshPortfolio } = usePortfolio(safeAddress, null);

  // Safe 合约创建引导弹窗
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  /* -------------------------------- Predict 充值相关 end -------------------------------- */

  /* -------------------------------- pd 支持的链/币（第三项 Tab）---------------- */
  const [pdChains, setPdChains] = useState<PredictBridgeChainItem[]>([]);
  const [pdTokenSymbols, setPdTokenSymbols] = useState<string[]>([]);
  const [tokenChainIds, setTokenChainIds] = useState<Record<string, string[]>>({});
  const [pdLoading, setPdLoading] = useState(false);
  const [pdSelectedTokenSymbol, setPdSelectedTokenSymbol] = useState<string>('');
  const [pdSelectedChainId, setPdSelectedChainId] = useState<string>('');
  const [pdDepositAddress, setPdDepositAddress] = useState<{
    btc: string;
    evm: string;
    svm: string;
    tron: string;
  } | null>(null);
  /* -------------------------------- pd end -------------------------------- */

  // 充值、划转tab切换
  const tableChangeHandle = (key: DepositType) => {
    setType(key);
    if (open) {
      captureDepositClick({ type: key, source });
    }
  };

  // 首屏加载、tab切换：根据 activeChain 设置默认网络与币种
  useEffect(() => {
    if (type === 'spot') {
      const chainIdStr = defaultChainId || String(chainList[0].chainId);
      const matchedChain = chainList.find(c => String(c.chainId) === chainIdStr) || chainList[0];
      setDepositSelectedChain(matchedChain);
      const matchedToken = matchedChain.tokenList.find(t => t.tokenContractAddress.toLowerCase() === defaultTokenAddress?.toLowerCase());
      setDepositSelectedToken(matchedToken || matchedChain.tokenList[0]);
    } else if (type === 'predict') {
      // 初始化 Predict 充值页面
      refreshPredictBalance();
      refreshPortfolio();
      recheckDeployment();
    }
  }, [type, defaultChainId, defaultTokenAddress]);

  // 当弹窗打开或 depositTab 变化时，同步更新 type 状态
  useEffect(() => {
    if (open) {
      setType(depositTab as DepositType);
      // 弹窗打开时检查 Session Signer 授权
      checkAndAuthorize();
      // 弹窗打开时检查并设置 HL 邀请码
      checkAndSetReferrer();
    } else {
      setType('');
    }
  }, [open, depositTab]);

  useEffect(() => {
    if (open && !prevDepositOpen.current) {
      captureDepositClick({ type: depositTab, source });
    }
    prevDepositOpen.current = open;
  }, [open, depositTab, source]);

  // 第三项 Tab：进入「预测」时拉取 pd supported-assets
  useEffect(() => {
    if (type !== 'predict' || !open) return;
    setPdLoading(true);
    Promise.allSettled([getSupportedAssets(), safeAddress ? postBridgeDeposit(safeAddress) : Promise.resolve(null)])
      .then(([assetsResult, depositResult]) => {
        const assetsRes = assetsResult.status === 'fulfilled' ? assetsResult.value : null;
        const depositRes = depositResult.status === 'fulfilled' ? depositResult.value : null;
        const list = (assetsRes as any)?.supportedAssets || [];
        // 充值地址（depositRes 可能为 null，需安全访问）
        setPdDepositAddress((depositRes as any)?.address || null);
        const res = convertPdAssets(list);
        setPdChains(res.pdChains);
        setPdTokenSymbols(res.pdTokenSymbols);
        setTokenChainIds(res.tokenChainIds);
        const defaultToken = res.pdTokenSymbols[0];
        // 设置默认网络和币种
        setPdSelectedTokenSymbol(defaultToken);
        setPdSelectedChainId(res.tokenChainIds[defaultToken.toLowerCase()][0] || '');
      })
      .finally(() => setPdLoading(false));
  }, [type, open, safeAddress]);

  // 现货充值：仅展示有 tokenList 的链
  const depositChainOptions = chainList.filter(c => c.tokenList?.length);
  const depositTokenOptions = depositSelectedChain?.tokenList ?? [];
  const depositAddress = depositSelectedChain?.chainId === 501 ? userInfo?.solWalletAddress || '' : evmAddress || '';
  // 第二项 Tab 固定：仅 Arbitrum + USDC
  const arbitrumChain = useMemo(() => chainList.find(c => c.chainId === 42161) ?? defaultChain, []);
  const arbitrumDepositAddress = evmAddress || '';

  // ---------- 兑换（perps 第二步）逻辑 start ----------
  const clearCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
    countdownAmountRef.current = '';
  };

  const amountFromIsValid = (_value?: string): { disabled: boolean; type: string } => {
    if (loadingSubmit) {
      setButtonDisabled(true);
      return { disabled: true, type: 'swapping' };
    }
    if (!isLogin) {
      setButtonDisabled(false);
      return { disabled: true, type: 'connectWallet' };
    }
    const val = _value ?? amountFrom;
    const walletAmount = tokenFrom.showBalance ?? '';
    const walletAmountBn = new BN(walletAmount);
    const valueBn = new BN(val);
    if (!val || valueBn.isZero()) {
      setButtonText(t('exchange.convert'));
      setButtonDisabled(true);
      return { disabled: true, type: '' };
    }
    if (valueBn.isGreaterThan(walletAmountBn)) {
      setButtonText(t('exchange.balanceInsufficient'));
      setButtonDisabled(true);
      return { disabled: true, type: 'insufficientBalance' };
    }
    if (tokenFrom.chainId === -1 && new BN(val).isLessThanOrEqualTo(1)) {
      setButtonText(t('exchange.convert'));
      setButtonDisabled(true);
      return { disabled: true, type: 'perpsMinWithdrawAmount' };
    }
    if (tokenTo.chainId === -1 && new BN(val).isLessThan(5)) {
      setButtonText(t('exchange.convert'));
      setButtonDisabled(true);
      return { disabled: true, type: 'perpsMinDepositAmount' };
    }
    setButtonText(t('exchange.convert'));
    setButtonDisabled(false);
    return { disabled: false, type: '' };
  };

  const amountFromChangeHandle = (val: string) => {
    const { status, value } = validAmount({ value: val });
    if (status === 'error') return;
    if (status === 'zero') {
      setAmountFrom(value);
      setShowSwapDetail(false);
      setAmountTo('');
      setButtonDisabled(true);
      setErrMsg('');
      clearCountdown();
      return;
    }
    setAmountFrom(value);
    if (!value) {
      setPriceData({});
      clearCountdown();
      return;
    }
    const { disabled, type } = amountFromIsValid(value);
    if (disabled && type === 'perpsMinWithdrawAmount') {
      setErrMsg(t('exchange.minWithdrawAmountPerps'));
      setButtonDisabled(true);
      return;
    }
    if (disabled && type === 'perpsMinDepositAmount') {
      setErrMsg(t('exchange.minDepositAmountPerps'));
      setButtonDisabled(true);
      return;
    }
    if (disabled || type === 'insufficientBalance') {
      setErrMsg('');
      setShowSwapDetail(false);
      if (debounceFetchRef.current) {
        clearTimeout(debounceFetchRef.current);
        debounceFetchRef.current = null;
      }
      clearCountdown();
      return;
    }
    setErrMsg('');
    setButtonDisabled(false);
    if (tokenFrom.chainId === -1 || isSame || (tokenTo.chainId === -1 && tokenFrom.tokenSymbol === 'USDC' && tokenFrom.chainId === 42161)) {
      if (tokenFrom.chainId === -1) {
        setAmountTo(new BN(value).minus(1).toString());
      } else {
        setAmountTo(value);
      }
      setShowSwapDetail(false);
      setLoadingPrice(false);
      clearCountdown();
      return;
    }
    // 目前仅支持合约充值，走不动这里，暂时先保留
    if (debounceFetchRef.current) {
      clearTimeout(debounceFetchRef.current);
    }
    clearCountdown();
    debounceFetchRef.current = setTimeout(() => {
      fetchConvertQuote(value);
    }, 200);
  };

  const setMaxAmountFromHandle = () => {
    const _min = chainFrom.minRetainedBalance ?? 0;
    setEstimateGasFee(_min);
    const _balance = Number(tokenFrom?.showBalance);
    if (tokenFrom.isMainTokenSymbol) {
      if (new BN(tokenFrom.showBalance || 0).isGreaterThanOrEqualTo(_min)) {
        const _realBalance = new BN(tokenFrom.showBalance || 0).minus(new BN(_min)).toString();
        setAmountFrom(_realBalance);
        amountFromChangeHandle(_realBalance);
      } else {
        message.warning(t('exchange.networkFeeReserve', { amount: _min, symbol: tokenFrom.tokenSymbol }));
      }
    } else {
      setAmountFrom('' + _balance);
      amountFromChangeHandle('' + _balance);
    }
  };

  const switchHandleChange = () => {
    const tempChain = chainFrom;
    setChainFrom(chainTo);
    setChainTo(tempChain);
    const temp = tokenFrom;
    setTokenFrom(tokenTo);
    if (tokenTo.chainId === -1) {
      setTokenTo(arbitrumUSDC);
    } else {
      setTokenTo(temp);
    }
    setAmountFrom('');
    setAmountTo('');
    setPriceData({});
    setShowSwapDetail(false);
    clearCountdown();
  };

  const fetchConvertQuote = async (val: string) => {
    setErrMsg('');
    setLoadingPrice(true);
    setButtonText(t('exchange.fetchingQuote'));
    const res = await getConvertQuote({
      srcChainId: chainFrom.chainId,
      srcToken: tokenFrom.tokenContractAddress,
      dstChainId: chainTo.chainId === -1 ? 42161 : chainTo.chainId,
      dstToken: tokenTo.tokenContractAddress,
      srcAmount: val,
    });
    const { quotes = [] } = res || {};
    if (quotes.length) {
      const data = quotes[0];
      setAmountTo(formatPriceWithoutComma({ price: data.dstAmount, decimal: 6, defaultZero: '0.00' }));
      const serviceFee = data.estimate?.feeCosts || [];
      let allFee = '0';
      let allFeeUSD = '0';
      serviceFee.forEach((item: { amount: string; amountUSD: string }) => {
        allFee = new BN(allFee).plus(new BN(item?.amount || 0)).toString();
        allFeeUSD = new BN(allFeeUSD).plus(new BN(item?.amountUSD || 0)).toString();
      });
      setPriceData({
        allFee: formatPriceWithoutComma({ price: allFee, decimal: 6, defaultZero: '0.00' }),
        allFeeUSD: formatPriceWithoutComma({ price: allFeeUSD, decimal: 6, defaultZero: '0.00' }),
        fromTokenUnitPrice: divide(data.dstAmount, data.srcAmount),
        toTokenUnitPrice: divide(data.srcAmount, data.dstAmount),
        gasCosts: data?.estimate?.gasCosts?.[0] || {},
        ...data,
      });
      setShowSwapDetail(true);
      setButtonDisabled(false);
      countdownAmountRef.current = val;
      setCountdown(COUNTDOWN_TIME);
    } else {
      message.error(t('exchange.systemBusy'));
      setPriceData({});
      setShowSwapDetail(false);
      setButtonDisabled(true);
      clearCountdown();
    }
    setLoadingPrice(false);
    setButtonText(t('exchange.convert'));
  };

  // 合约充值
  const spotToPerpsHandle = async () => {
    clearCountdown();
    setLoadingSubmit(true);
    const params = {
      fromChainId: chainFrom.chainId,
      fromTokenAddress: tokenFrom.tokenContractAddress,
      amount: amountFrom,
      minOutAmount: '0',
    };
    const res = await convertExecute(params);
    if (res?.depositTxHash) {
      capturePaymentResult({ status: 'success', type: 'spot_to_perps' });
      message.success(t('exchange.convertSuccess'), 5);
      setLoadingSubmit(false);
      onSuccess?.();
      setPerpsStep(0);
      onClose();
    } else {
      capturePaymentResult({ status: 'fail', type: 'spot_to_perps', msg: 'no_deposit_tx_hash' });
      message.error(t('exchange.convertFailed'));
      setLoadingSubmit(false);
    }
  };

  // 合约提现
  const perpsToSpotHandle = async () => {
    setLoadingSubmit(true);
    const destination = evmAddress;
    if (!destination) {
      capturePaymentResult({ status: 'fail', type: 'perps_to_spot', msg: 'no_destination' });
      message.error(t('action.login'));
      setLoadingSubmit(false);
      return;
    }
    if (!isReady) {
      capturePaymentResult({ status: 'fail', type: 'perps_to_spot', msg: 'wallet_not_ready' });
      message.error(t('perps.networkNotMatch'));
      setLoadingSubmit(false);
      return;
    }
    try {
      const result = await submitPerpsWithdraw3({
        amount: amountFrom,
        destination,
        signTypedData,
        switchChain,
      });
      if (!result.ok) {
        if (result.userRejected) {
          capturePaymentResult({ status: 'fail', type: 'perps_to_spot', msg: 'user_rejected' });
          message.error(t('perps.userCancelledSignature'));
        } else {
          capturePaymentResult({
            status: 'fail',
            type: 'perps_to_spot',
            msg: result.message.slice(0, 200),
          });
          message.error(result.message);
        }
        return;
      }
      capturePaymentResult({ status: 'success', type: 'perps_to_spot' });
      message.success(t('perps.withdrawSuccess'), 5);
      onSuccess?.();
      setPerpsStep(0);
      onClose();
    } finally {
      setLoadingSubmit(false);
    }
  };

  const spotToSpotHandle = async () => {
    clearCountdown();
    setLoadingSubmit(true);
    const res = await spotExchange({
      fromChainId: chainFrom.chainId,
      fromTokenAddress: tokenFrom.tokenContractAddress,
      toChainId: chainTo.chainId,
      toTokenAddress: tokenTo.tokenContractAddress,
      amount: amountFrom,
      minOutAmount: '0',
    });
    if (res?.success) {
      capturePaymentResult({ status: 'success', type: 'spot_to_spot' });
      message.success(t('exchange.convertSuccess'), 5);
      setLoadingSubmit(false);
      onSuccess?.();
      setPerpsStep(0);
      onClose();
    } else {
      capturePaymentResult({ status: 'fail', type: 'spot_to_spot', msg: 'spot_exchange_failed' });
      message.error(t('exchange.convertFailed'));
      setLoadingSubmit(false);
    }
  };

  const convertHandle = async () => {
    if (userInfo?.isWalletBlacklisted) {
      setRiskModalOpen(true);
      return;
    }
    const { disabled } = amountFromIsValid();
    if (isSame || disabled || loadingSubmit) return;
    if (tokenTo.chainId === -1) {
      spotToPerpsHandle();
    } else if (tokenFrom.chainId === -1) {
      perpsToSpotHandle();
    } else {
      spotToSpotHandle();
    }
  };

  const fetchHlBalanceData = async () => {
    if (!evmAddress) return;
    const balanceData: any = await getClearinghouseState(evmAddress);
    const balance = formatPriceWithoutComma({
      price: balanceData?.withdrawable || 0,
      decimal: 6,
      defaultZero: '0.00',
    });
    perpsUSDC.showBalance = balance;
    setTokenTo(perpsUSDC);
  };

  const fetchTokens = async () => {
    const tokens = await getAllTokens({
      chainId: defaultChain.chainId.toString(),
      walletAddress: defaultChain.chainId === 501 ? userInfo?.solWalletAddress : userInfo?.evmWalletAddress || '',
    });
    const arbUsdc = tokens.find((item: TokenInfo) => item.tokenSymbol.toLowerCase() === 'usdc') as TokenInfo;
    arbitrumUSDC.showBalance = formatPriceWithoutComma({
      price: arbUsdc?.balance || 0,
      decimal: 6,
      defaultZero: '0.00',
    });
    setTokenFrom(arbUsdc || ({} as TokenInfo));
    setChainFrom(defaultChain);
    setChainTo(convertChains[convertChains.length - 1]);
  };

  const initPerpsPage = () => {
    setInitLoading(true);
    Promise.all([fetchTokens(), fetchHlBalanceData()]).then(() => {
      setInitLoading(false);
    });
  };

  // 重置合约充值页面
  const resetPerpsPage = () => {
    setAmountFrom('');
    setAmountTo('');
    setTokenFrom({} as TokenInfo);
    setTokenTo(perpsUSDC);
    setChainFrom(defaultChain);
    setChainTo(convertChains[convertChains.length - 1]);
    setPriceData({});
    setShowSwapDetail(false);
    setButtonText(t('exchange.convert'));
    setButtonDisabled(true);
    setErrMsg('');
    if (debounceFetchRef.current) {
      clearTimeout(debounceFetchRef.current);
      debounceFetchRef.current = null;
    }
    clearCountdown();
  };

  // 充值标题
  const depositTitle = useMemo((): React.ReactNode => {
    if (chainFrom.chainId === -1) {
      return t('exchange.convertTitlePerpsToSpot');
    }
    if (chainTo.chainId === -1) {
      const text = t('exchange.convertTitleSpotPerps');
      return (
        <>
          {text}
          <span className="cursor-pointer text-primary hover:underline" onClick={() => setPerpsStep(0)}>
            {t('exchange.depositNow')}
          </span>
        </>
      );
    }
    return t('exchange.convertTitleSpotToSpot', {
      fromChainName: chainFrom.chainName,
      fromTokenSymbol: tokenFrom.tokenSymbol,
      toChainName: chainTo.chainName,
      toTokenSymbol: tokenTo.tokenSymbol,
    });
  }, [chainFrom, chainTo, tokenFrom, tokenTo, t]);

  useEffect(() => {
    if (!showSwapDetail) {
      setErrMsg('');
      return;
    }
    if (Number(tokenFrom?.mainBalance || 0) < (chainFrom.minRetainedBalance ?? 0)) {
      setErrMsg(t('exchange.gasInsufficient'));
      setButtonDisabled(true);
    } else if (tokenTo.chainId === -1 && Number(priceData?.dstAmount) <= 5) {
      setErrMsg(t('exchange.minDepositAmountPerps'));
      setButtonDisabled(true);
    } else {
      setErrMsg('');
      setButtonDisabled(false);
    }
  }, [priceData, showSwapDetail, tokenFrom, chainFrom, tokenTo]);

  useEffect(() => {
    amountFromIsValid();
  }, [tokenFrom]);

  useEffect(() => {
    if (countdown > 0) {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            const refreshAmount = countdownAmountRef.current;
            if (refreshAmount && !loadingPrice && !loadingSubmit) {
              fetchConvertQuote(refreshAmount);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    }
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [countdown]);

  useEffect(() => {
    if (type === 'perps' && perpsStep === 1) {
      initPerpsPage();
    }
  }, [type, perpsStep]);

  useEffect(() => {
    if (type !== 'perps') {
      setPerpsStep(0);
      resetPerpsPage();
    }
  }, [type]);
  // ---------- 兑换（perps 第二步）逻辑 end ----------

  const pdSelectedChainInfo = useMemo((): ChainInfo | null => {
    if (!pdSelectedTokenSymbol) return null;
    // 优先用当前选中的 chainId,保证切换网络后 Min 金额等信息同步更新
    const chainId = pdSelectedChainId || tokenChainIds[pdSelectedTokenSymbol.toLowerCase()]?.[0];
    if (!chainId) return null;
    const c = pdChains.find(ch => ch.chainId === chainId);
    if (!c) return null;
    return {
      chainId: Number(chainId),
      chainIndex: Number(chainId),
      chainName: c.chainName,
      chainNameForWallet: c.chainName,
      dexTokenApproveAddress: '',
      img: c.logo,
      type: '',
      rpcUrl: '',
      mainTokenAddress: '',
      mainTokenSymbol: '',
      mainTokenDecimals: '',
      tradeDetailUrl: '',
      hashUrl: '',
      minRetainedBalance: c.minCheckoutUsd,
      tokenList: [],
    } as ChainInfo;
  }, [pdSelectedTokenSymbol, pdSelectedChainId, pdChains]);

  const HelpLink = (
    <div className="flex items-center gap-2 text-xs">
      <HelpCircle className="w-[12px] h-[12px]" />
      {t('exchange.haveQuestionsText')}
      <span
        className="text-xs text-primary cursor-pointer hover:underline"
        onClick={() => {
          window.open('https://docs.billion.live/qa/user-guide', '_blank');
        }}
      >
        {t('exchange.getHelp')}
      </span>
    </div>
  );

  // 复用：现货充值主体（网络/币种选择 or 只读展示 + 二维码与地址，可选 submitButton，可选 Collapse）
  const renderDepositPanel = (opts: {
    chain: ChainInfo;
    token: TokenInfo | null;
    readOnly: boolean;
    address: string;
    type: DepositType;
    collapseConfig?: { label: React.ReactNode; children?: React.ReactNode };
    submitButton?: { label: string; onClick: () => void };
  }) => {
    const { chain, token, readOnly, address, collapseConfig, submitButton } = opts;
    return (
      <div className="deposit-v2-token-list-wrap">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            {type === 'spot' && token && (
              <>
                <label className="block text-sm font-medium text-font-secondary mb-2">{t('exchange.depositCurrency')}</label>
                <Select
                  className="w-full"
                  value={depositSelectedToken?.tokenContractAddress}
                  onChange={tokenContractAddress => {
                    const tkn = depositTokenOptions.find(t => t.tokenContractAddress === tokenContractAddress);
                    if (tkn) setDepositSelectedToken(tkn);
                  }}
                  options={depositTokenOptions.map(tkn => ({
                    value: tkn.tokenContractAddress,
                    label: (
                      <span className="flex items-center gap-2">
                        <img src={tkn.tokenLogoUrl} alt="" className="w-5 h-5 rounded-full" />
                        {tkn.tokenSymbol}
                      </span>
                    ),
                  }))}
                  optionLabelProp="label"
                  getPopupContainer={node => node.parentElement || document.body}
                />
              </>
            )}
          </div>
          <div>
            {!readOnly && chain && (
              <>
                <label className="block text-sm font-medium text-font-secondary mb-2 flex justify-between">
                  {t('exchange.depositNetwork')}
                  {type === 'spot' && (
                    <span className="flex items-center">
                      <span className="text-font-primary/50 text-xs">Min ${chain?.minRetainedBalance ?? 0}</span>
                      <Tooltip
                        title={t('qrCodeModal.minDepositTip', {
                          amount: chain?.minRetainedBalance,
                          symbol: chain?.mainTokenSymbol,
                        })}
                      >
                        <Info className="w-3 h-3 ml-1 text-[#909090]" />
                      </Tooltip>
                    </span>
                  )}
                  {type === 'perps' && (
                    <span className="flex items-center">
                      <span className="text-font-primary/50 text-xs">Min $5</span>
                      <Tooltip title={t('exchange.minDepositAmountPerps')}>
                        <Info className="w-3 h-3 ml-1 text-[#909090]" />
                      </Tooltip>
                    </span>
                  )}
                </label>
                <Select
                  className="w-full"
                  value={depositSelectedChain?.chainId}
                  onChange={chainId => {
                    const c = depositChainOptions.find(ch => ch.chainId === chainId);
                    if (c) {
                      setDepositSelectedChain(c);
                      const first = c.tokenList?.[0];
                      setDepositSelectedToken(first ?? null);
                    }
                  }}
                  options={depositChainOptions.map(c => ({
                    value: c.chainId,
                    label: (
                      <span className="flex items-center gap-2">
                        <img src={c.img} alt="" className="w-5 h-5 rounded-full" />
                        {c.chainName}
                      </span>
                    ),
                  }))}
                  optionLabelProp="label"
                  getPopupContainer={node => node.parentElement || document.body}
                />
              </>
            )}
          </div>
        </div>
        <div className={`flex flex-col items-center${type === 'spot' ? ' py-4' : ''}`}>
          {address ? (
            <>
              <QRCode value={address} size={180} bordered icon={chain?.img} className="mb-4" />
              <div className="w-full mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-font-secondary">{t('qrCodeModal.receivingAddress')}</span>
                  <span
                    className="text-xs text-primary cursor-pointer hover:underline"
                    onClick={() => {
                      onClose();
                      if (type === 'perps') {
                        navigate('/portfolio/perps');
                      } else {
                        navigate('/portfolio/spot');
                      }
                    }}
                  >
                    {t('header.assetCenter')}
                  </span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-background-3/10 dark:bg-[#141414]">
                  <span className="text-sm text-font-primary">{address}</span>
                  <CopyText text={address} showText={false} className="shrink-0" />
                </div>
              </div>
              {submitButton && (
                <Button type="primary" className="w-full mt-1" size="large" onClick={submitButton.onClick}>
                  {submitButton.label}
                </Button>
              )}
              {collapseConfig?.children ? (
                <Collapse
                  className="deposit-collapse-container mt-2 w-full rounded-lg bg-background-3/10 dark:bg-[#141414] [&_.ant-collapse-item]:border-0 [&_.ant-collapse-header]:text-font-primary [&_.ant-collapse-header]:py-3 [&_.ant-collapse-header]:flex [&_.ant-collapse-header]:flex-row-reverse [&_.ant-collapse-header]:justify-between"
                  ghost
                  items={[
                    {
                      key: '1',
                      label: collapseConfig.label,
                      children: collapseConfig.children ?? undefined,
                    },
                  ]}
                />
              ) : (
                // 只有一行的，不使用 collapse 组件，直接显示一行
                <div className="w-full mt-2 px-3 py-3 rounded-lg bg-background-3/10 dark:bg-[#141414]">{collapseConfig?.label}</div>
              )}
            </>
          ) : (
            <div className="text-sm text-font-gray py-8">{t('exchange.connectWalletToShowAddress')}</div>
          )}
        </div>
      </div>
    );
  };

  // 第三项 Tab：pd 支持的链/币（数据来自 supported-assets），选中的币种下不支持的链置为 disable
  const renderPdDepositPanel = () => {
    if (pdLoading) {
      return (
        <div className="deposit-v2-token-list-wrap">
          <div className="grid grid-cols-2 gap-4 mb-2">
            <Skeleton className="w-full h-[20px] rounded-sm bg-background-3/5" />
            <Skeleton className="w-full h-[20px] rounded-sm bg-background-3/5" />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Skeleton className="w-full h-[32px] rounded-lg bg-background-3/5" />
            <Skeleton className="w-full h-[32px] rounded-lg bg-background-3/5" />
          </div>
          <div className="flex flex-col items-center py-8">
            <Skeleton className="w-[180px] h-[180px] rounded-lg bg-background-3/5" />
          </div>
          <div className="flex flex-col items-center py-8">
            <Skeleton className="w-full h-[44px] rounded-lg bg-background-3/5" />
          </div>
        </div>
      );
    }
    const chainInfo = pdSelectedChainInfo;
    // 根据链 chainId 获取对应类型的充值地址
    let address = '';
    const chainId = chainInfo?.chainId;
    if (chainId === SOLANA_CHAIN_ID) {
      address = pdDepositAddress?.svm || '';
    } else if (chainId === BITCOIN_CHAIN_ID) {
      address = pdDepositAddress?.btc || '';
    } else if (chainId === TRON_CHAIN_ID) {
      address = pdDepositAddress?.tron || '';
    } else {
      address = pdDepositAddress?.evm || '';
    }
    const chainIdsForSelectedToken = pdSelectedTokenSymbol ? tokenChainIds[pdSelectedTokenSymbol.toLowerCase()] : undefined;
    return (
      <div className="deposit-v2-token-list-wrap">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-font-secondary mb-2">{t('exchange.depositCurrency')}</label>
            <Select
              className="w-full"
              value={pdSelectedTokenSymbol || undefined}
              onChange={symbol => {
                if (symbol !== pdSelectedTokenSymbol && pdSelectedChainId) {
                  setPdSelectedTokenSymbol(symbol);
                  const ids = tokenChainIds[symbol.toLowerCase()];
                  setPdSelectedChainId(ids?.includes(pdSelectedChainId) ? pdSelectedChainId : ids[0]);
                }
              }}
              options={pdTokenSymbols.map(symbol => ({
                value: symbol,
                label: (
                  <div className="flex items-center">
                    {pdTokenLogos[symbol] ? (
                      <img src={pdTokenLogos[symbol]} alt="" className="w-5 h-5 rounded-full inline-block mr-2 align-middle" />
                    ) : (
                      <span className="w-5 h-5 rounded-full inline-flex mr-2 align-middle bg-gray-300 dark:bg-gray-600 items-center justify-center text-font-primary text-xs font-medium">
                        {symbol.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="text-font-primary">{symbol}</span>
                  </div>
                ),
              }))}
              getPopupContainer={node => node.parentElement || document.body}
              placeholder={t('exchange.depositCurrency')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-font-secondary mb-2 flex justify-between">
              <span>{t('exchange.depositNetwork')}</span>
              {chainInfo && (
                <span className="flex items-center">
                  <span className="text-font-primary/50 text-xs">Min ${chainInfo.minRetainedBalance ?? 0}</span>
                  <Tooltip
                    title={t('qrCodeModal.minDepositTip', {
                      amount: chainInfo.minRetainedBalance,
                      symbol: pdSelectedTokenSymbol || '',
                    })}
                  >
                    <Info className="w-3 h-3 ml-1 text-[#909090]" />
                  </Tooltip>
                </span>
              )}
            </label>
            <Select
              className="w-full"
              value={pdSelectedChainId || undefined}
              onChange={chainId => setPdSelectedChainId(chainId)}
              optionLabelProp="label"
              labelRender={() => {
                const value = pdSelectedChainId;
                const ch = pdChains.find(c => c.chainId === value);
                return (
                  <span>
                    <img src={ch?.logo} alt="" className="w-5 h-5 rounded-full inline-block mr-2 align-middle" />
                    {ch?.chainName}
                  </span>
                );
              }}
              options={pdChains.map(ch => {
                const disabled = !chainIdsForSelectedToken?.includes(ch.chainId);
                return {
                  value: ch.chainId,
                  label: (
                    <div className="flex items-center justify-between">
                      <span className={disabled ? 'opacity-50' : ''}>
                        <img src={ch.logo} alt="" className="w-5 h-5 rounded-full inline-block mr-2 align-middle" />
                        {ch.chainName}
                      </span>
                      <span className="text-font-primary/50 text-xs mr-2 ml-2">Min ${ch.minCheckoutUsd}</span>
                    </div>
                  ),
                  disabled,
                };
              })}
              getPopupContainer={node => node.parentElement || document.body}
              placeholder={t('exchange.depositNetwork')}
            />
          </div>
        </div>
        <div className="flex flex-col items-center py-4">
          {address ? (
            <>
              <QRCode value={address} size={180} bordered icon={chainInfo?.img || ''} className="mb-4" />
              <div className="w-full mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-font-secondary">{t('qrCodeModal.receivingAddress')}</span>
                  <span
                    className="text-xs text-primary cursor-pointer hover:underline"
                    onClick={() => {
                      onClose();
                      navigate('/portfolio/predict');
                    }}
                  >
                    {t('header.assetCenter')}
                  </span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-background-3/10 dark:bg-[#141414]">
                  <span className="text-sm text-font-primary">{address}</span>
                  <CopyText text={address} showText={false} className="shrink-0" />
                </div>
              </div>
              <Collapse
                className="deposit-collapse-container mt-2 w-full rounded-lg bg-background-3/10 dark:bg-[#141414] [&_.ant-collapse-item]:border-0 [&_.ant-collapse-header]:text-font-primary [&_.ant-collapse-header]:py-3 [&_.ant-collapse-header]:flex [&_.ant-collapse-header]:flex-row-reverse [&_.ant-collapse-header]:justify-between"
                ghost
                items={[
                  {
                    key: '1',
                    label: (
                      <span className="flex items-center gap-2 text-xs">
                        <Clock className="w-[12px] h-[12px]" />
                        <span>{t('exchange.processingTime')}</span>
                      </span>
                    ),
                    children: HelpLink,
                  },
                ]}
              />
            </>
          ) : (
            <div className="text-sm text-font-gray py-8">{t('exchange.connectWalletToShowAddress')}</div>
          )}
        </div>
      </div>
    );
  };

  const tabItems: TabsProps['items'] = [
    {
      key: 'spot',
      label: t('exchange.spot'),
      children: renderDepositPanel({
        chain: depositSelectedChain,
        token: depositSelectedToken,
        readOnly: false,
        address: depositAddress,
        type: 'spot',
        collapseConfig: {
          label: (
            <span className="flex items-center gap-2 text-xs">
              <Clock className="w-[12px] h-[12px] text-font-primary" />
              <span>{t('exchange.processingTime')}</span>
            </span>
          ),
          children: HelpLink,
        },
      }),
    },
    {
      key: 'perps',
      label: t('exchange.perps'),
      children: (
        <>
          <Steps
            current={perpsStep}
            onChange={step => setPerpsStep(step)}
            style={{ width: 300, margin: '12px auto 12px' }}
            items={[
              { title: <div className="text-sm">{t('exchange.perpsStep1')}</div> },
              { title: <div className="text-sm">{t('exchange.perpsStep2')}</div> },
            ]}
          />
          {perpsStep === 0 &&
            renderDepositPanel({
              chain: arbitrumChain,
              token: arbitrumUSDC,
              readOnly: true,
              address: arbitrumDepositAddress,
              type: 'perps',
              collapseConfig: {
                label: (
                  <span className="flex items-center gap-2 text-xs">
                    <CircleDollarSign className="w-[12px] h-[12px]" />
                    <span>{t('exchange.perpsMinDepositTip')}</span>
                  </span>
                ),
              },
              submitButton: { label: t('exchange.goToTransfer'), onClick: () => setPerpsStep(1) },
            })}
          {perpsStep === 1 && (
            <div>
              {initLoading ? (
                <>
                  <Skeleton className="w-full h-[32px] rounded-xl mb-4 bg-background-3/5" />
                  <div className="flex flex-col relative">
                    <Skeleton className="w-full h-[124px] rounded-xl bg-background-3/5" />
                    <Skeleton className="swap-switch-skeleton w-12 h-12 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background-3/5" />
                    <Skeleton className="w-full h-[124px] rounded-xl mt-4 bg-background-3/5" />
                  </div>
                  <Skeleton className="w-full h-[32px] mt-4 bg-background-3/5" />
                </>
              ) : (
                <>
                  <div className="text-gray-400 text-xs mb-4">{depositTitle}</div>
                  <div className="swap-page-content-1 relative">
                    <div className="swap-content bg-background-3/5 dark:bg-[#141414] rounded-xl p-4">
                      <div className="flex flex-col items-start gap-1">
                        <div className="flex items-center justify-between gap-2 cursor-pointer w-full">
                          <div className="flex items-center gap-2 cursor-pointer">
                            <span className="text-font-primary/70 text-sm">{t('swap.from')}</span>
                            <Chain {...chainFrom} width={20} height={20} showName />
                          </div>
                          {Boolean(isLogin) &&
                            (initLoading ? (
                              <Skeleton className="w-[32px] h-[16px] rounded bg-background-3/5" />
                            ) : (
                              <div className="flex items-center">
                                <Wallet className="w-3 h-3 text-gray-400" />
                                <span className="text-[#909090] text-xs ml-2" style={{ fontFamily: 'Montserrat Regular' }}>
                                  {tokenFrom.showBalance}
                                </span>
                                {Number(tokenFrom?.showBalance) > 0 && (
                                  <span className="text-xs text-primary ml-2 cursor-pointer" onClick={setMaxAmountFromHandle}>
                                    {t('swap.max')}
                                  </span>
                                )}
                                {tokenFrom.isMainTokenSymbol && estimateGasFee > 0 && (
                                  <span>
                                    <Tooltip
                                      title={t('exchange.networkFeeReserve', {
                                        amount: estimateGasFee,
                                        symbol: tokenFrom.tokenSymbol,
                                      })}
                                    >
                                      <Info className="w-3 h-3 ml-1 text-[#909090]" />
                                    </Tooltip>
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between mt-4 w-full">
                          <div className="flex items-center gap-2 cursor-pointer">
                            <Token {...tokenFrom} width={32} height={32} hideAddress hideChain />
                          </div>
                          <Input
                            placeholder="0.0"
                            value={amountFrom}
                            onChange={e => amountFromChangeHandle(e.target.value)}
                            style={{ fontSize: '16px', background: 'transparent', paddingRight: '0' }}
                            className="flex-1 text-right border-none bg-transparent hover:bg-transparent focus:bg-transparent focus:ring-0 focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0 !border-none !ring-0 !outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none dark:text-font-primary placeholder:text-font-primary/50"
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="w-full text-right text-[#909090] text-xs h-[16px]" style={{ fontFamily: 'Montserrat Regular' }}>
                        {Number(priceData?.estimate?.fromAmountUSD) ? '$' + priceData?.estimate?.fromAmountUSD : ''}
                      </div>
                    </div>
                    <div
                      className="swap-switch-wrap flex items-center justify-center my-1 cursor-pointer text-font-reverse"
                      onClick={switchHandleChange}
                    >
                      <SwitchIcon onClick={switchHandleChange} />
                    </div>
                    <div className="swap-content bg-background-3/5 dark:bg-[#141414] rounded-xl p-4 mt-4">
                      <div className="flex flex-col items-start gap-1">
                        <div className="flex items-center justify-between gap-2 cursor-pointer w-full">
                          <div className="flex items-center gap-2">
                            <span className="text-font-primary/70 text-sm">{t('exchange.to')}</span>
                            <Chain {...chainTo} width={20} height={20} showName />
                          </div>
                          {Boolean(isLogin) &&
                            (initLoading ? (
                              <Skeleton className="w-[32px] h-[16px] rounded bg-background-3/5" />
                            ) : (
                              <div className="flex items-center gap-2">
                                <Wallet className="w-3 h-3 text-gray-400" />
                                <span className="text-[#909090] text-xs" style={{ fontFamily: 'Montserrat Regular' }}>
                                  {tokenTo.showBalance}
                                </span>
                              </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between mt-4 w-full">
                          <div className="flex items-center gap-2 cursor-pointer">
                            <Token {...tokenTo} width={32} height={32} hideAddress hideChain />
                          </div>
                          <div className="flex-1 text-right border-none bg-transparent focus:ring-0 focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0 !border-none !ring-0 !outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none">
                            {amountTo ? (
                              <>
                                <span className="text-[#909090] text-xs mr-2">Est</span>
                                <span className="text-font-primary">{amountTo}</span>
                              </>
                            ) : (
                              <span className="text-font-primary/50">0.0</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-full text-right text-[#909090] text-xs h-[16px]" style={{ fontFamily: 'Montserrat Regular' }}>
                        {Number(priceData?.estimate?.toAmountUSD) ? '$' + priceData?.estimate?.toAmountUSD : ''}
                      </div>
                    </div>
                  </div>
                  {showSwapDetail && (
                    <div className="swap-info-card bg-background-3/10 rounded-2xl mt-4 p-6 flex flex-col gap-4 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-font-primary/70">{t('exchange.referenceRate')}</span>
                        <span className="text-right font-semibold flex items-center gap-1">
                          {loadingPrice ? (
                            <Skeleton className="w-32 h-4 rounded bg-background-3/5" />
                          ) : (
                            <span>
                              1 {tokenFrom.tokenSymbol} ≈ {priceData.fromTokenUnitPrice} {tokenTo.chainId === -1 ? 'USDC' : tokenTo.tokenSymbol}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-font-primary/70">{t('exchange.minReceiveAmount')}</span>
                        <span className="text-right font-semibold flex items-center gap-1">
                          {loadingPrice ? (
                            <Skeleton className="w-32 h-4 rounded bg-background-3/5" />
                          ) : (
                            <span>
                              {priceData?.dstAmountMin} {tokenTo.chainId === -1 ? 'USDC' : tokenTo.tokenSymbol}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-font-primary/70 flex items-center gap-1">{t('exchange.estimatedNetworkFee')}</span>
                        <span className="text-right font-semibold">
                          {loadingPrice ? (
                            <Skeleton className="w-16 h-4 rounded bg-background-3/5" />
                          ) : (
                            <span>
                              {priceData?.allFee} {tokenFrom.tokenSymbol}
                              <span className="text-[#909090]">(${priceData?.allFeeUSD})</span>
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-font-primary/70 flex items-center gap-1">{t('exchange.gasFee')}</span>
                        <span className="text-right font-semibold">
                          {loadingPrice ? (
                            <Skeleton className="w-16 h-4 rounded bg-background-3/5" />
                          ) : (
                            <span>
                              {formatPriceWithoutComma({ price: priceData?.gasCosts?.amount || '0', decimal: 6, defaultZero: '0.00' })}{' '}
                              {chainFrom.mainTokenSymbol}
                              <span className="text-[#909090]">
                                (${formatPriceWithoutComma({ price: priceData?.gasCosts?.amountUSD || '0', decimal: 6, defaultZero: '0.00' })})
                              </span>
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-font-primary/70 flex items-center gap-1">{t('exchange.serviceFee')}</span>
                        <span className="text-right font-semibold">
                          {loadingPrice ? <Skeleton className="w-16 h-4 rounded bg-background-3/5" /> : <span>0%</span>}
                        </span>
                      </div>
                    </div>
                  )}
                  {isSame ? (
                    <div className="mt-2 text-xs text-gray-500">{t('exchange.sameSourceAndTarget')}</div>
                  ) : (
                    <>{errMsg && <div className="mt-2 text-xs text-red-500">{errMsg}</div>}</>
                  )}
                  <Button
                    className="w-full mt-4"
                    size="large"
                    type="primary"
                    loading={loadingSubmit || loadingPrice}
                    disabled={buttonDisabled || isSame}
                    onClick={convertHandle}
                  >
                    {buttonText}
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'predict',
      label: t('exchange.predict'),
      children: renderPdDepositPanel(),
    },
  ];

  return (
    <>
      <Modal
        maskClosable={false}
        open={open}
        onCancel={onClose}
        footer={null}
        width={500}
        title={<div className="text-center text-lg">{t('exchange.deposit')}</div>}
      >
        <Tabs className="mt-2" activeKey={type} items={tabItems} onChange={(key: string) => tableChangeHandle(key as DepositType)}></Tabs>
      </Modal>
      <RiskModal open={riskModalOpen} onClose={() => setRiskModalOpen(false)} />
      {showSetupGuide && (
        <Suspense fallback={null}>
          <SetupGuideModal open={showSetupGuide} onOpenChange={setShowSetupGuide} onComplete={() => recheckDeployment()} />
        </Suspense>
      )}
    </>
  );
};

export default DepositV2;
